import { CdkScrollable } from '@angular/cdk/scrolling';
import { NgFor, AsyncPipe } from '@angular/common';
import { Component, ElementRef, ViewChild, inject } from '@angular/core';
import { FormBuilder, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
} from '@angular/material/dialog';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatSelectChange, MatSelect, MatOption } from '@angular/material/select';
import { ExtendedNotificationComponent } from '@next/core-lib/components';
import {
  AbstractDialog,
  AbstractDialogData,
  AbstractDialogResultData,
} from '@next/core-lib/dialog';
import { FormComponent } from '@next/core-lib/form';
import { TranslatePipe, AgrDatePipe } from '@next/core-lib/i18n';
import { SiteState } from '@next/core-lib/site';
import { Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Barn, Cycle } from 'src/app/cycle/models/cycle.model';
import { getCycles } from 'src/app/cycle/store/selectors/cycle.selectors';

import { IntervalType } from '../../models/within-cycle-performance.model';

export interface ImportDialogInputData extends AbstractDialogData {
  selectedCycle: Cycle;
  intervalTypes?: IntervalType[];
}

interface ImportDialogOutputModel {
  file: File;
  cycleId: number;
  barnId: number;
  intervalType: IntervalType;
}

@Component({
  selector: 'app-import',
  templateUrl: './import.component.html',
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    FormComponent,
    ReactiveFormsModule,
    ExtendedNotificationComponent,
    MatFormField,
    MatLabel,
    MatSelect,
    NgFor,
    MatOption,
    MatButton,
    MatDialogActions,
    AsyncPipe,
    TranslatePipe,
    AgrDatePipe,
  ],
})
export class ImportComponent extends AbstractDialog<ImportDialogOutputModel> {
  private readonly store = inject<Store<SiteState>>(Store);
  data = inject<ImportDialogInputData>(MAT_DIALOG_DATA);
  protected mdDialogRef =
    inject<MatDialogRef<ImportComponent, AbstractDialogResultData<ImportDialogOutputModel>>>(
      MatDialogRef,
    );

  @ViewChild('file', { static: true }) inputFile: ElementRef<HTMLInputElement>;
  selectedFile: File;
  selectedCycle: Cycle;
  barns: Barn[] = [];
  intervalTypes: IntervalType[] = [];

  cycles$: Observable<Cycle[]> = combineLatest([this.store.select(getCycles)]).pipe(
    map(([cycles]: [Cycle[]]) => {
      const preselectCycle = cycles.find((c) => c.id === this.selectedCycle.id);

      if (preselectCycle) {
        this.formGroup.controls.cycleControl.setValue(preselectCycle, {
          emitEvent: false,
        });
      }

      this.refreshBarns();

      return cycles
        .filter((c) => c.dayOldChicks?.length > 0)
        .sort((a, b) => (a.cycleNumber < b.cycleNumber ? 1 : -1));
    }),
  );

  formGroup = new FormBuilder().group({
    cycleControl: new FormControl<Cycle>(null, [Validators.required]),
    barnControl: new FormControl<Barn>(null, [Validators.required]),
    intervalTypeControl: new FormControl<IntervalType>(null, [Validators.required]),
  });

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    super();
    this.selectedCycle = this.data.selectedCycle;
    this.intervalTypes = this.data.intervalTypes;
  }

  onFileSelected() {
    this.selectedFile = this.inputFile.nativeElement.files[0];
  }

  changeCycleSelection(selection: MatSelectChange) {
    this.selectedCycle = selection.value;
    this.refreshBarns();
  }

  private refreshBarns() {
    this.barns = [];
    this.selectedCycle.dayOldChicks?.forEach((d) => {
      if (!this.barns.find((b) => b.id === d.barn?.id)) {
        this.barns.push(d.barn);
      }
    });

    if (this.barns.length === 1) {
      this.formGroup.controls.barnControl.setValue(this.barns[0], {
        emitEvent: false,
      });
    }
  }
}
